import { supabase } from '../lib/supabase';
import { normalizeImageForUpload } from './heicConvert';

// Brand-level images (logos + decorative elements) live in their own bucket
// and table so they are reusable across every project of the brand. The
// shape mirrors `projectAssets.ts` but scoped by `brand_profile_id` instead
// of `project_id` and only handles images.
//
// DB schema (see migration in chat history):
//   table  : public.brand_assets
//   columns: id, brand_profile_id, id_company, tipo, name, storage_path,
//            mime_type, size_bytes, width, height, sort_order, created_at
//   bucket : 'brand-assets'  (private)
//   path   : `${id_company}/${brand_profile_id}/${tipo}/${assetId}.${ext}`
export type BrandAssetTipo = 'logo' | 'element' | 'font';

// Font files we accept for brand-typography uploads. Modern web-friendly
// formats only — TTF/OTF still work but woff2 is preferred for size.
const FONT_MIME_TYPES = new Set([
  'font/woff2', 'font/woff', 'font/ttf', 'font/otf',
  'application/font-woff2', 'application/font-woff',
  'application/x-font-ttf', 'application/x-font-otf',
  'application/octet-stream', // some browsers report this for ttf/otf
]);
const FONT_EXTS = new Set(['woff2', 'woff', 'ttf', 'otf']);

export interface BrandAsset {
  id: string;
  brand_profile_id: string;
  id_company: string;
  tipo: BrandAssetTipo;
  name: string | null;
  storage_path: string;
  mime_type: string | null;
  size_bytes: number | null;
  width: number | null;
  height: number | null;
  sort_order: number;
  created_at: string;
}

const BUCKET = 'brand-assets';

// Same caching strategy as projectAssets: keep signed URLs in memory for
// 50min so re-renders don't hammer the Storage API. Supabase signs them
// for 1h, the 10min margin keeps us safe from clock skew.
const signedUrlCache = new Map<string, { url: string; expiresAt: number }>();
const SIGNED_URL_TTL_SECONDS = 3600;
const SIGNED_URL_CACHE_MS = 50 * 60 * 1000;

function extForFile(file: File): string {
  const m = file.name.match(/\.([a-zA-Z0-9]+)$/);
  if (m) return m[1].toLowerCase();
  const sub = file.type.split('/')[1];
  return sub || 'bin';
}

async function probeImageDimensions(file: File): Promise<{ width: number; height: number } | null> {
  return new Promise(resolve => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    img.src = url;
  });
}

export interface UploadBrandAssetParams {
  file: File;
  brandProfileId: string;
  companyId: string;
  tipo: BrandAssetTipo;
}

/**
 * Upload a brand asset to Storage and insert a row in `brand_assets`.
 * Handles two flavors:
 *   - images (logo / element): HEIC/AVIF normalization + dimensions probe.
 *   - font files (woff2/woff/ttf/otf): uploaded as-is, no probing.
 * The `tipo` field decides which path is taken.
 */
export async function uploadBrandAsset(
  { file, brandProfileId, companyId, tipo }: UploadBrandAssetParams,
): Promise<BrandAsset> {
  const isFont = tipo === 'font';
  if (isFont) {
    const ext = extForFile(file);
    const looksLikeFont = FONT_MIME_TYPES.has(file.type) || FONT_EXTS.has(ext);
    if (!looksLikeFont) {
      throw new Error(`Solo se aceptan archivos de fuente (woff2, woff, ttf, otf). Recibido: ${file.type || ext || 'desconocido'}`);
    }
  } else if (!file.type.startsWith('image/')) {
    throw new Error(`Only image files are accepted as brand assets (got ${file.type || 'unknown'})`);
  }

  if (!isFont) file = await normalizeImageForUpload(file);
  const ext = extForFile(file);
  const assetId = crypto.randomUUID();
  const storagePath = `${companyId}/${brandProfileId}/${tipo}/${assetId}.${ext}`;

  // Probe image dimensions in parallel with the upload — fonts skip this.
  const probePromise = isFont ? Promise.resolve(null) : probeImageDimensions(file);

  const { error: uploadErr } = await supabase.storage
    .from(BUCKET)
    .upload(storagePath, file, { contentType: file.type || undefined, upsert: false });
  if (uploadErr) {
    throw new Error(`Storage upload failed: ${uploadErr.message}`);
  }

  const probed = await probePromise;

  const row: Omit<BrandAsset, 'created_at'> = {
    id: assetId,
    brand_profile_id: brandProfileId,
    id_company: companyId,
    tipo,
    name: file.name,
    storage_path: storagePath,
    mime_type: file.type || null,
    size_bytes: file.size,
    width: probed?.width ?? null,
    height: probed?.height ?? null,
    sort_order: 0,
  };

  const { data, error: insertErr } = await supabase
    .from('brand_assets')
    .insert(row)
    .select('*')
    .single();

  if (insertErr || !data) {
    // Best-effort cleanup if the row insert fails after the upload
    // succeeded — otherwise the file would dangle in storage.
    await supabase.storage.from(BUCKET).remove([storagePath]).catch(() => {});
    throw new Error(`Brand asset insert failed: ${insertErr?.message || 'unknown'}`);
  }

  return data as BrandAsset;
}

/**
 * Cached signed URL helper for brand assets. Mirrors `getAssetSignedUrl`
 * from projectAssets but targets the brand bucket.
 */
export async function getBrandAssetSignedUrl(storagePath: string): Promise<string | null> {
  const cached = signedUrlCache.get(storagePath);
  const now = Date.now();
  if (cached && cached.expiresAt > now) return cached.url;

  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(storagePath, SIGNED_URL_TTL_SECONDS);
  if (error || !data?.signedUrl) return null;

  signedUrlCache.set(storagePath, { url: data.signedUrl, expiresAt: now + SIGNED_URL_CACHE_MS });
  return data.signedUrl;
}

/**
 * Fetch all brand assets for a given brand profile, optionally filtered by
 * `tipo`. Returns rows ordered by `sort_order` ASC then newest first so
 * future drag-to-reorder support keeps a stable visual order.
 */
export async function fetchBrandAssets(
  brandProfileId: string,
  tipo?: BrandAssetTipo,
): Promise<BrandAsset[]> {
  let q = supabase
    .from('brand_assets')
    .select('*')
    .eq('brand_profile_id', brandProfileId)
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: false });
  if (tipo) q = q.eq('tipo', tipo);
  const { data, error } = await q;
  if (error || !data) return [];
  return data as BrandAsset[];
}

/**
 * Delete a brand asset (storage object + DB row). Best-effort: if storage
 * deletion fails we still drop the row so the UI doesn't show a ghost.
 */
export async function deleteBrandAsset(asset: Pick<BrandAsset, 'id' | 'storage_path'>): Promise<void> {
  await supabase.storage.from(BUCKET).remove([asset.storage_path]).catch(() => {});
  await supabase.from('brand_assets').delete().eq('id', asset.id);
  signedUrlCache.delete(asset.storage_path);
}
